import { useQuery } from "@tanstack/react-query";
import packsDataService from "features/Licensing/Packs/services/packsDataService";
import { type CardReportingItem } from "components/cards/CardReporting/CardReporting";
import { type PaginationParams } from "features/Library/Common/models";
import { type FetchPageResult } from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import { roundToTwoDigits } from "features/Library/Common/utils/roundToTwoDigits";
import { type PerformanceWithAccountFilter } from "features/Library/Common/utils/performanceUtils";
import type { AddOnsAccountRow, AddOnDetailRow } from "features/Reporting/types/addOns";

export const useAddOnsEngagementQuery = (filter: PerformanceWithAccountFilter) => {
  return useQuery({
    queryKey: ["addOnsEngagement", filter],
    queryFn: async ({ signal }) => {
      const { data } = await packsDataService.getAddOnsEngagement(filter, signal);

      const cards: CardReportingItem[] = [
        {
          stat: data.TotalCustomers,
          statDescription: "Customers",
          popupBody: "Total number of customers.",
        },
        {
          stat: data.TotalActiveAddOns,
          statDescription: "Total Add-ons",
          popupBody: "Total number of add-ons (active and expired) enabled for customers.",
        },
        {
          stat: roundToTwoDigits(data.AverageCustomerAddOns),
          statDescription: "Avg. Add-Ons/Customer",
          popupBody: "Average number of add-ons enabled for customers.",
        },
      ];

      return { cards };
    },
    enabled: filter.type !== undefined,
  });
};

export const createAddOnsAccountsFetcher = (filter: PerformanceWithAccountFilter) => {
  return async (params: PaginationParams, signal?: AbortSignal): Promise<FetchPageResult<AddOnsAccountRow>> => {
    const requestParams = {
      ...filter,
      ...params,
    };

    const response = await packsDataService.getAddOnsAccounts(requestParams, signal);

    return {
      rows: response.data.map((item) => ({ ...item, id: item.CustomerId })),
      totalCount: parseInt(response.headers[process.env.REACT_APP_COUNT_HEADER_NAME!]),
    };
  };
};

export const createAccountAddOnsFetcher = (customerId: number, filter: PerformanceWithAccountFilter) => {
  return async (params: PaginationParams, signal?: AbortSignal): Promise<FetchPageResult<AddOnDetailRow>> => {
    const requestParams: PerformanceWithAccountFilter = {
      ...filter,
      accounts: [customerId],
      includeMyData: false,
      ...params,
    };

    const { data, headers } = await packsDataService.getAccountAddOns(requestParams, signal);

    return {
      rows: data.map((item) => ({ ...item, id: `${item.CustomerId} ${item.AddonId}` })),
      totalCount: parseInt(headers[process.env.REACT_APP_COUNT_HEADER_NAME!]),
    };
  };
};
